/**
 * The connect-apply intent, carried OUT OF BAND of the URL.
 *
 * `/jobs-handoff?id=<id>&intent=apply` is supposed to hand the job screen a
 * `?apply=1` query param (see jobs-handoff.tsx). It cannot: `/jobs/<id>` is
 * ambiguous between the (app) and (employer) route groups, and the bounce the
 * (employer) layout performs to resolve that ambiguity
 * (`useJobsDeepLinkPassthrough`) re-serialises the route from the parsed
 * navigation state. `apply` is not a segment of `(employer)/jobs/[id]`, so
 * expo-router drops it before the layout ever sees it — verified at runtime:
 * the layout observes `useGlobalSearchParams() === { id }` and a
 * `window.location.search` that still belongs to the PREVIOUS (handoff) URL.
 * The observed chain is
 *   /jobs-handoff?id=…&intent=apply
 *     → /jobs/<id>?id=<id>        (apply already gone)
 *     → /jobs/<id>
 *
 * So the intent rides in module state instead, which no route re-serialisation
 * can touch. jobs-handoff arms it at the moment it redirects; the job screen
 * opens the dialog from it. `?apply=1` still works when it does survive (a
 * direct link, a future router fix) — this is a second, additive channel, not
 * a replacement.
 *
 * ── Why the intent is NOT consumed on open ──────────────────────────────────
 * The canonical user is a FIRST-TIME applicant, and their journey mounts the
 * job screen twice. After sign-in the job screen mounts, then the
 * post-onboarding tour immediately replaces it with /chat; only when the tour
 * is skipped or finished does the real job screen mount. A single-consume flag
 * is burned by that first, transient mount and the dialog never appears on the
 * second — which is precisely the journey this whole handoff exists for.
 *
 * So the intent survives until it has actually been ACTED ON: the job screen
 * peeks (`hasPendingConnectApplyIntent`) to decide whether to open, and clears
 * only when the dialog is dismissed or submitted, or when the job it names
 * turns out to have nothing to open. An unmount clears nothing, so the second
 * mount opens the dialog exactly as the first would have.
 *
 * Deliberately NOT persisted: an intent that outlived the JS context would
 * ambush a later cold start with a consent dialog. Cross-reload continuity is
 * already handled by `savePostAuthTarget('jobs:<id>:apply')`, which lands the
 * user back on /jobs-handoff and re-arms this.
 */
import { fromCatalogJobRouteId } from '@tabbio/utils';

/**
 * Generous, because the gap between arming and the dialog can now include a
 * whole post-onboarding tour. It is a backstop, not the primary bound: the
 * intent is already cleared on dismissal, on submission, on landing on a
 * DIFFERENT job, and by the JS context ending. The only thing it guards is an
 * intent that was armed and then never arrived anywhere.
 */
const INTENT_TTL_MS = 5 * 60_000;

let pending: { catalogJobId: string; armedAt: number } | null = null;

/** Accepts either id form (`native-<id>` route id or `native:<id>` catalog id). */
export function setPendingConnectApplyIntent(jobId: string | null | undefined) {
  const catalogJobId = jobId ? fromCatalogJobRouteId(jobId) : '';
  if (!catalogJobId) return;
  pending = { catalogJobId, armedAt: Date.now() };
}

/**
 * Does an armed intent name this job? A peek, NOT a take — see the header: the
 * dialog may be torn down before the user ever sees it, so only an actual
 * dismissal/submission clears (`clearPendingConnectApplyIntent`).
 *
 * It does drop the intent in the two cases where it can no longer be honoured:
 * expired, or the user has arrived at a DIFFERENT job (leaving it armed there
 * would fire a consent dialog on some later, unrequested visit to the original
 * job). A falsy `jobId` is "not resolved yet" and clears nothing.
 */
export function hasPendingConnectApplyIntent(jobId: string | null | undefined) {
  if (!pending) return false;
  if (Date.now() - pending.armedAt > INTENT_TTL_MS) {
    pending = null;
    return false;
  }
  if (!jobId) return false;
  if (fromCatalogJobRouteId(jobId) !== pending.catalogJobId) {
    pending = null;
    return false;
  }
  return true;
}

/** The intent has been acted on (or superseded): drop it. */
export function clearPendingConnectApplyIntent() {
  pending = null;
}
